import { Link } from "react-router-dom";
import { Check, Mail, BookOpen, Inbox, ArrowRight, Download } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Eyebrow } from "@/components/ui-kit";
import { Seo } from "@/components/Seo";
import { ASSETS } from "@/lib/content";

const STEPS = [
  {
    icon: Inbox,
    title: "Check your email",
    body: "Your Reader Preview is on its way.",
  },
  {
    icon: BookOpen,
    title: "Read Chapter One",
    body: "Take your time. Highlight. Reflect. Pray.",
  },
  {
    icon: Mail,
    title: "Continue the Journey",
    body:
      "Over the next two weeks you'll receive thoughtful emails about faith, purpose, mindset, stewardship, leadership, and building a life that matters.",
  },
];

export default function ThankYou() {
  return (
    <div data-testid="thank-you-page">
      <Seo
        title="Welcome to The Shift"
        description="Your Advance Reader Preview is on its way. Welcome to the journey."
        path="/thank-you"
      />

      {/* HERO */}
      <section className="relative bg-black noise pt-40 pb-24 lg:pt-52 lg:pb-32 overflow-hidden">
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <div
              className="mx-auto w-20 h-20 lg:w-24 lg:h-24 border border-[#D4AF37] rounded-full flex items-center justify-center"
              aria-hidden="true"
            >
              <Check size={38} className="text-[#D4AF37]" strokeWidth={1.5} />
            </div>
          </Reveal>
          <Reveal delay={100}>
            <Eyebrow className="mt-10">You're In</Eyebrow>
          </Reveal>
          <Reveal delay={160}>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mt-6 leading-[1.06] tracking-tight">
              Welcome to <span className="text-[#D4AF37] italic">The Shift</span>
            </h1>
          </Reveal>
          <Reveal delay={240}>
            <p className="font-body text-lg lg:text-xl text-white/60 font-light mt-8 leading-relaxed max-w-2xl mx-auto">
              Your Advance Reader Preview is on its way to your inbox. Check your inbox over the
              next few minutes. If you don't see it, check Promotions, Spam, or Junk. Move the
              email to your Primary inbox so you don't miss future notes.
            </p>
          </Reveal>

          {/* Instant PDF access — because a reader shouldn't have to wait. */}
          <Reveal delay={320}>
            <div className="mt-12 flex flex-col items-center">
              <a
                href={ASSETS.readerPreviewPdf}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="thank-you-download-pdf"
                data-track="download"
                data-track-asset="reader_preview_pdf"
                data-track-location="thank_you"
                className="inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#E6C75A] text-black font-body font-medium text-[15px] px-8 py-4 transition-all duration-300 group"
              >
                <Download size={16} />
                Download the Preview Now
              </a>
              <p className="font-body text-xs tracking-[0.16em] uppercase text-white/35 mt-4">
                Or wait — it's coming to your inbox
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PERSONAL NOTE */}
      <section className="bg-white text-black py-24 lg:py-32">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <Eyebrow className="!text-[#B8952E]">While You're Waiting</Eyebrow>
          </Reveal>
          <Reveal delay={100}>
            <div
              className="mt-10 border border-black/10 bg-white p-10 lg:p-14 shadow-[0_20px_60px_-30px_rgba(0,0,0,0.25)]"
              data-testid="personal-note"
            >
              <div className="w-10 h-px bg-[#D4AF37] mb-8" />
              <div className="space-y-6 font-body text-lg text-black/75 leading-relaxed">
                <p className="text-black text-xl">
                  I want to personally thank you for downloading <em>The Shift</em>.
                </p>
                <p>This isn't just another book.</p>
                <p>
                  It's a conversation. One that started with a question I couldn't ignore.
                </p>
                <p className="font-display italic text-2xl lg:text-3xl text-[#B8952E] leading-snug">
                  What am I really building?
                </p>
                <p>
                  My prayer is that these pages challenge the way you think, strengthen your
                  faith, and encourage you to build the life God created you for.
                </p>
              </div>
              <div className="mt-10 flex items-center gap-4">
                <div className="w-10 h-px bg-black/20" />
                <p className="font-display text-lg text-black">Don Orr Jr.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* STEPS TIMELINE */}
      <section className="bg-[#0a0a0a] noise py-24 lg:py-32">
        <div className="relative z-10 max-w-[1180px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl">
            <Reveal>
              <Eyebrow>The Journey</Eyebrow>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mt-6 leading-tight tracking-tight">
                Here's what happens next
              </h2>
            </Reveal>
          </div>

          <ol className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-16" data-testid="steps-timeline">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 90}>
                <li
                  data-testid={`step-${i + 1}`}
                  className="relative h-full border border-white/12 p-8 lg:p-10 hover:border-[#D4AF37]/60 transition-colors duration-500 group"
                >
                  <div className="flex items-center gap-4">
                    <span className="font-display text-[#D4AF37] text-3xl">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <s.icon size={20} className="text-white/50 group-hover:text-[#D4AF37] transition-colors" />
                  </div>
                  <h3 className="font-display text-2xl text-white mt-6 leading-snug">{s.title}</h3>
                  <p className="font-body text-white/55 mt-4 leading-relaxed text-[15px]">{s.body}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative bg-black noise py-28 lg:py-40 text-center overflow-hidden">
        <div className="relative z-10 max-w-2xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Ready to Continue?</Eyebrow>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mt-7 leading-[1.1] tracking-tight">
              If you're already excited to continue reading…
            </h2>
          </Reveal>
          <Reveal delay={180}>
            <p className="font-body text-lg text-white/60 mt-7 leading-relaxed">
              You don't have to wait. Get the full book today.
            </p>
          </Reveal>
          <Reveal delay={260}>
            <div className="mt-11 flex justify-center">
              <Link
                to="/checkout"
                data-testid="thank-you-get-book"
                data-track="cta_click"
                data-track-label="get_the_book"
                data-track-location="thank_you"
                className="inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#E6C75A] text-black font-body font-medium text-[15px] px-11 py-5 transition-all duration-300 group"
              >
                Get The Full Book
                <ArrowRight
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SIGNOFF */}
      <section className="bg-white py-16 text-center">
        <div className="max-w-xl mx-auto px-6">
          <Reveal>
            <p className="font-display italic text-2xl lg:text-3xl text-black leading-snug">
              Keep Building.
            </p>
            <p className="font-body text-sm tracking-[0.24em] uppercase text-black/50 mt-4">
              — Don Orr Jr.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
